import React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/back-navigation";
import { ScrollNavigation } from "@/components/scroll-navigation";
import {
  Download,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Minus,
  AlertCircle,
  FileText,
  Calendar,
  User,
  Home,
  Award,
  RotateCcw,
  Edit,
  ClipboardList
} from "lucide-react";
import { Bell } from "lucide-react";
import AuditProgressAndScore from "@/components/AuditProgressAndScore";
import { format } from "date-fns";
import jsPDF from "jspdf";

export function ViewCompletedAuditPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  const urlParams = new URLSearchParams(window.location.search);
  const auditId = urlParams.get('auditId');
  
  // Fetch session
  const { data: session } = useQuery({
    queryKey: ["/api/session"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/session");
      return response.json();
    }
  });
  
  // Fetch audit
  const { data: audit, isLoading: auditLoading } = useQuery({
    queryKey: ["/api/audits", auditId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/audits/${auditId}`);
      return response.json();
    },
    enabled: !!auditId
  });
  
  // Fetch template
  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: ["/api/templates", audit?.template_id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/templates/${audit.template_id}`);
      return response.json();
    },
    enabled: !!audit?.template_id
  });
  
  // Fetch action items for this audit
  const { data: actionItems = [] } = useQuery({
    queryKey: ["/api/audits", auditId, "actions"],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/audits/${auditId}/actions`);
      return response.json();
    },
    enabled: !!auditId && audit?.status === 'Submitted'
  });

  // Fetch home details for scope checks (always call hook; enabled controls network)
  const { data: home } = useQuery({
    queryKey: ["/api/homes", audit?.home_id],
    queryFn: async () => {
      if (!audit?.home_id) return null;
      const res = await apiRequest("GET", `/api/homes/${audit.home_id}`);
      return res.json();
    },
    enabled: !!audit?.home_id
  });
  
  // Modal state for manager actions on drafts assigned to others (must be before any early return)
  const [openAssignedModal, setOpenAssignedModal] = React.useState(false);
  React.useEffect(() => {
    const currentUser = session?.user;
    const isManager = currentUser && ['HomeManager','OrgManager','Developer','PlatformManager'].includes(currentUser.role);
    const isAssignee = typeof audit?.assigned_to === 'number' && currentUser?.id === audit?.assigned_to;
    const isCreator = currentUser?.id === audit?.created_by;
    const canEditDraft = audit?.status === 'Draft' && (isAssignee || (!audit?.assigned_to && isCreator));
    if (audit?.status === 'Draft' && !canEditDraft && isManager) {
      setOpenAssignedModal(true);
    }
  }, [audit?.status, audit?.assigned_to, audit?.created_by, session?.user?.id, session?.user?.role]);
  
  // Parse template content
  const items = template?.content?.items || [];
  const answerableItems = items.filter((item: any) => 
    item.type !== 'heading' && item.type !== 'divider' && item.type !== 'section'
  );
  const responses = audit?.responses_json || {};
  
  const handleExportPDF = () => {
    if (!audit || !template) return;

    // Declare variables in outer scope so helper functions can access them
    let doc: any = undefined;
    let pageWidth = 0;
    let pageHeight = 0;
    const margin = 20;
    const lineHeight = 6;
    let yPosition = margin;

    try {
      doc = new jsPDF();
      pageWidth = doc.internal.pageSize.getWidth();
      pageHeight = doc.internal.pageSize.getHeight();
      yPosition = margin;
    } catch (err: any) {
      console.error('Failed to export PDF', err);
      toast({ title: 'Export error', description: err?.message || 'Failed to generate PDF', variant: 'destructive' });
      return;
    }
    
    // Helper function to sanitize text for PDF encoding
    const sanitizeText = (text: string): string => {
      if (!text) return '';
      // Replace special characters that cause encoding issues
      return text
        .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters
        .replace(/&/g, 'and')
        .replace(/€/g, 'EUR')
        .replace(/£/g, 'GBP')
        .replace(/['']/g, "'")
        .replace(/[""]/g, '"')
        .replace(/…/g, '...')
        .replace(/–|—/g, '-');
    };
    
    // Helper function to check if we need a new page
    const checkPageBreak = (requiredSpace: number) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };
    
    // Helper function to add wrapped text
    const addWrappedText = (text: string, x: number, fontSize: number, maxWidth: number, isBold = false) => {
      const sanitized = sanitizeText(text);
      doc.setFontSize(fontSize);
      if (isBold) {
        doc.setFont('helvetica', 'bold');
      }
      const lines = doc.splitTextToSize(sanitized, maxWidth);
      lines.forEach((line: string) => {
        checkPageBreak(lineHeight);
        doc.text(line, x, yPosition);
        yPosition += lineHeight;
      });
      if (isBold) {
        doc.setFont('helvetica', 'normal');
      }
    };
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(sanitizeText("Audit Report"), pageWidth / 2, yPosition, { align: "center" });
    yPosition += 10;
    doc.setFont('helvetica', 'normal');
    
    // Audit metadata
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(sanitizeText("Audit Details"), margin, yPosition);
    yPosition += lineHeight;
    doc.setFont('helvetica', 'normal');
    
    doc.text(sanitizeText(`Name: ${audit.name || 'Untitled'}`), margin, yPosition);
    yPosition += lineHeight;
    
    if (audit.description) {
      doc.text(sanitizeText(`Description: ${audit.description || 'N/A'}`), margin, yPosition);
      yPosition += lineHeight;
    }
    
    doc.text(sanitizeText(`Template: ${template.name || 'N/A'}`), margin, yPosition);
    yPosition += lineHeight;
    
    doc.text(sanitizeText(`Status: ${audit.status || 'N/A'}`), margin, yPosition);
    yPosition += lineHeight;
    
    if (audit.score !== null && audit.score !== undefined) {
      doc.text(sanitizeText(`Score: ${audit.score}%`), margin, yPosition);
      yPosition += lineHeight;
    }
    
    if (audit.created_at) {
      doc.text(sanitizeText(`Created: ${format(new Date(audit.created_at), "dd/MM/yyyy")}`), margin, yPosition);
      yPosition += lineHeight;
    }
    
    if (audit.submitted_at) {
      doc.text(sanitizeText(`Submitted: ${format(new Date(audit.submitted_at), "dd/MM/yyyy")}`), margin, yPosition);
      yPosition += lineHeight;
    }
    
    if (audit.approved_at) {
      doc.text(sanitizeText(`Approved: ${format(new Date(audit.approved_at), "dd/MM/yyyy")}`), margin, yPosition);
      yPosition += lineHeight;
    }
    
    yPosition += 5;
    
    // Questions and Answers
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(sanitizeText("Audit Responses"), margin, yPosition);
    yPosition += 10;
    doc.setFont('helvetica', 'normal');
    
    answerableItems.forEach((item: any, idx: number) => {
      const response = responses[item.id];
      
      checkPageBreak(25);
      
      // Question number and label
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      addWrappedText(`${idx + 1}. ${item.label || 'Question'}`, margin, 10, pageWidth - 2 * margin, true);
      doc.setFont('helvetica', 'normal');
      
      if (item.description) {
        doc.setFontSize(9);
        doc.setTextColor(100);
        addWrappedText(`   ${item.description || ''}`, margin, 9, pageWidth - 2 * margin);
        doc.setTextColor(0);
      }
      
      // Response based on type
      doc.setFontSize(10);
      if (item.type === 'ynna_actions_review') {
        if (response?.answer) {
          doc.text(sanitizeText(`   Answer: ${response.answer || 'N/A'}`), margin, yPosition);
          yPosition += lineHeight;
          
          if (item.actions_enabled && response.actions) {
            if (response.actions.noActionNeeded) {
              doc.text(sanitizeText(`   Actions: No actions needed`), margin, yPosition);
              yPosition += lineHeight;
            } else if (response.actions.actionText) {
              doc.text(sanitizeText(`   Actions:`), margin, yPosition);
              yPosition += lineHeight;
              addWrappedText(`   ${response.actions.actionText || ''}`, margin, 10, pageWidth - 2 * margin);
            }
          }
        } else {
          doc.setTextColor(150);
          doc.text(sanitizeText(`   No answer provided`), margin, yPosition);
          doc.setTextColor(0);
          yPosition += lineHeight;
        }
      } else if (Array.isArray(response)) {
        if (response.length > 0) {
          doc.text(sanitizeText(`   ${response.join(', ')}`), margin, yPosition);
          yPosition += lineHeight;
        } else {
          doc.setTextColor(150);
          doc.text(sanitizeText(`   No answer provided`), margin, yPosition);
          doc.setTextColor(0);
          yPosition += lineHeight;
        }
      } else if (response) {
        addWrappedText(`   ${String(response)}`, margin, 10, pageWidth - 2 * margin);
      } else {
        doc.setTextColor(150);
        doc.text(sanitizeText(`   No answer provided`), margin, yPosition);
        doc.setTextColor(0);
        yPosition += lineHeight;
      }
      
      yPosition += 3;
    });
    
    // Attachments section
    if (audit.attachments && Array.isArray(audit.attachments) && audit.attachments.length > 0) {
      checkPageBreak(20);
      yPosition += 5;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeText("Attachments"), margin, yPosition);
      yPosition += lineHeight + 2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      audit.attachments.forEach((attachment: any, index: number) => {
        checkPageBreak(lineHeight * 2);
        const fileSize = Math.round(attachment.fileSize / 1024);
        const uploadedDate = attachment.uploadedAt ? format(new Date(attachment.uploadedAt), "dd/MM/yyyy HH:mm") : "Unknown";
        doc.text(sanitizeText(`${index + 1}. ${attachment.fileName} (${fileSize}KB)`), margin + 5, yPosition);
        yPosition += lineHeight;
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(sanitizeText(`   Uploaded: ${uploadedDate}`), margin + 5, yPosition);
        yPosition += lineHeight;
        doc.setTextColor(0);
        doc.setFontSize(10);
      });
      yPosition += 3;
    }
    
    // Action Items Review section
    if (actionItems && actionItems.length > 0) {
      checkPageBreak(20);
      yPosition += 5;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeText("Action Items"), margin, yPosition);
      yPosition += lineHeight + 2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      actionItems.forEach((action: any, index: number) => {
        checkPageBreak(lineHeight * 6);
        
        doc.setFont('helvetica', 'bold');
        doc.text(sanitizeText(`${index + 1}. ${action.item_label}`), margin + 5, yPosition);
        yPosition += lineHeight;
        doc.setFont('helvetica', 'normal');
        
        addWrappedText(`   ${action.action_description}`, margin + 5, 10, pageWidth - 2 * margin - 10);
        
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(sanitizeText(`   Status: ${action.status || 'Pending'}`), margin + 5, yPosition);
        yPosition += lineHeight;
        
        if (action.actioned_at) {
          doc.text(`   Actioned: ${format(new Date(action.actioned_at), "dd/MM/yyyy")}`, margin + 5, yPosition);
          yPosition += lineHeight;
        }
        
        if (action.acknowledged) {
          doc.text(`   Acknowledged: ${format(new Date(action.acknowledged_at), "dd/MM/yyyy")}`, margin + 5, yPosition);
          yPosition += lineHeight;
        }
        
        doc.setTextColor(0);
        doc.setFontSize(10);
        
        if (action.review_notes) {
          doc.setFont('helvetica', 'bold');
          doc.text(`   Reviewer Notes:`, margin + 5, yPosition);
          yPosition += lineHeight;
          doc.setFont('helvetica', 'normal');
          addWrappedText(`   ${action.review_notes}`, margin + 5, 9, pageWidth - 2 * margin - 10);
        }
        
        yPosition += 3;
      });
      yPosition += 3;
    }
    
    // Footer
    const timestamp = format(new Date(), "dd/MM/yyyy HH:mm");
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(`Generated on ${timestamp} | Smart Care Audits`, margin, pageHeight - 10);
    doc.setTextColor(0);
    
    // Download - produce a Blob and trigger a proper download so browsers/Playwright see a real PDF
    try {
      const arrayBuffer = doc.output('arraybuffer');
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const fileName = `audit-${audit.id}-${format(new Date(), "yyyy-MM-dd")}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'Export started', description: `Preparing ${fileName} for download` });
    } catch (err: any) {
      console.error('Failed to save PDF', err);
      toast({ title: 'Export error', description: err?.message || 'Failed to download PDF', variant: 'destructive' });
    }
  };
  
  // Reopen audit mutation
  const reopenMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/audits/${auditId}/reopen`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/audits", auditId] });
      queryClient.invalidateQueries({ queryKey: ["/api/audits"] });
      toast({
        title: "Audit reopened",
        description: "The audit has been changed back to draft status. You can now edit it.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reopen audit. Please try again.",
        variant: "destructive",
      });
    },
  });

  // UI state: open-actions modal when approval is blocked
  const [openActionsModal, setOpenActionsModal] = React.useState(false);

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/audits/${auditId}/approve`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/audits", auditId] });
      queryClient.invalidateQueries({ queryKey: ["/api/audits"] });
      toast({ title: 'Success', description: 'Audit approved' });
    },
    onError: async (err: any) => {
      // Try to surface a friendly modal when server returns ACTIONS_OPEN
      try {
        const apiErr = err?.apiError || err?.body || err?.error || null;
        if (apiErr && apiErr.code === 'ACTIONS_OPEN') {
          setOpenActionsModal(true);
          return;
        }
        // apiRequest throws Error with "status: bodyText"; bodyText is JSON for API errors
        const msg: string = err?.message || '';
        const colon = msg.indexOf(':');
        const jsonText = colon >= 0 ? msg.slice(colon + 1).trim() : '';
        const parsed = jsonText.startsWith('{') ? JSON.parse(jsonText) : null;
        if (parsed && parsed.code === 'ACTIONS_OPEN') {
          setOpenActionsModal(true);
          return;
        }
      } catch {}
      toast({ title: 'Error', description: err?.message || 'Failed to approve audit', variant: 'destructive' });
    }
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async (notes: string) => {
      const response = await apiRequest("POST", `/api/audits/${auditId}/reject`, { rejection_notes: notes });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/audits", auditId] });
      queryClient.invalidateQueries({ queryKey: ["/api/audits"] });
      toast({ title: 'Success', description: 'Audit rejected' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err?.message || 'Failed to reject audit', variant: 'destructive' });
    }
  });
  
  if (!auditId) {
    return (
      <div className="p-6">
        <Card className="p-8">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Audit Selected</h3>
            <p className="text-gray-600 mb-4">Please select an audit to view.</p>
            <Button onClick={() => setLocation('/audits')}>Go to Audits</Button>
          </div>
        </Card>
      </div>
    );
  }
  
  if (auditLoading || templateLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }
  
  if (!audit || !template) {
    return (
      <div className="p-6">
        <Card className="p-8">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Audit Not Found</h3>
            <p className="text-gray-600 mb-4">The requested audit could not be loaded.</p>
            <Button onClick={() => setLocation('/audits')}>Go to Audits</Button>
          </div>
        </Card>
      </div>
    );
  }

  const currentUser = session?.user;
  const canApproveRole = currentUser && ['HomeManager', 'OrgManager', 'Developer', 'PlatformManager'].includes(currentUser.role);
  const isInScope = (() => {
    if (!currentUser) return false;
    if (['Developer', 'PlatformManager'].includes(currentUser.role)) return true;
    if (['HomeManager', 'HomeAdmin', 'HomeStaff'].includes(currentUser.role)) {
      return typeof audit.home_id === 'number' && audit.home_id === currentUser.home_id;
    }
    if (['OrgManager', 'OrgAdmin'].includes(currentUser.role)) {
      if (!audit.home_id) return false;
      // if we fetched home, compare organization_id; otherwise be conservative and return false
      return home ? (home.organization_id && currentUser.organization_id && home.organization_id === currentUser.organization_id) : false;
    }
    return false;
  })();
  const canApprove = !!canApproveRole && !!isInScope;
  const isManagerRole = currentUser && ['HomeManager', 'OrgManager', 'Developer', 'PlatformManager'].includes(currentUser.role);
  const isAssignee = typeof audit?.assigned_to === 'number' && currentUser?.id === audit?.assigned_to;
  const isCreator = currentUser?.id === audit?.created_by;
  const canEditDraft = audit?.status === 'Draft' && (isAssignee || (!audit?.assigned_to && isCreator));
  const canManageTakeover = !!isManagerRole && !!isInScope;
  // Diagnostic: log why approve might be disabled for debugging in browser devtools
  if (process.env.NODE_ENV !== 'production') {
    console.debug('Approve gating:', {
      currentUserRole: currentUser?.role,
      currentUserHomeId: currentUser?.home_id,
      auditHomeId: audit?.home_id,
      canApproveRole,
      isInScope,
      canApprove
    });
  }
  
  // Calculate filled responses count
  const filledCount = answerableItems.filter((item: any) => {
    const response = responses[item.id];
    if (response === null || response === undefined || response === '') return false;
    if (Array.isArray(response) && response.length === 0) return false;
    
    // For any question with actions enabled, check if answer is provided (response is an object)
    if (item.actions_enabled && typeof response === 'object' && !Array.isArray(response)) {
      return !!response.answer && response.answer !== '';
    }
    
    // For ynna_actions_review (legacy check, but keeping for backwards compatibility)
    if (item.type === 'ynna_actions_review' && response.answer) return true;
    if (item.type === 'ynna_actions_review' && !response.answer) return false;
    
    return true;
  }).length;
  
  const progressPercent = answerableItems.length > 0
    ? (filledCount / answerableItems.length) * 100
    : 0;

  // Takeover/Notify action handlers

  const handleNotifyAssignee = async () => {
    try {
      await apiRequest('POST', `/api/audits/${auditId}/reminder`);
      toast({ title: 'Reminder sent', description: 'The assignee has been notified.' });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message || 'Failed to send reminder', variant: 'destructive' });
    }
  };

  const handleTakeOver = async () => {
    try {
      await apiRequest('POST', `/api/audits/${auditId}/lock/force`, { ttlSeconds: 120 });
      toast({ title: 'Takeover successful', description: 'You now have edit access to this audit.' });
      setOpenAssignedModal(false);
      setLocation(`/audits/take?auditId=${auditId}`);
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message || 'Failed to take over audit', variant: 'destructive' });
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader title={audit.name} />
      
      {/* Header Card */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Card className="p-8 mb-6">
          {/* Title and Actions Row */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <FileText className="w-10 h-10 text-primary" />
              <div>
                <h1 className="page-title text-3xl font-bold text-foreground">{audit.name}</h1>
                <p className="text-sm text-muted-foreground mt-1">{template.name}</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={handleExportPDF}
                className="gap-2"
                data-testid="button-export-pdf"
              >
                <Download className="w-4 h-4" />
                Export PDF
              </Button>
              
              {audit.status === 'Draft' && canEditDraft && (
                <Button 
                  onClick={() => setLocation(`/audits/take?auditId=${auditId}`)}
                  className="gap-2"
                  data-testid="button-edit-audit"
                >
                  <Edit className="w-4 h-4" />
                  Edit Audit
                </Button>
              )}
              
              {session?.user && 
               ['HomeManager', 'OrgManager', 'Developer', 'PlatformManager'].includes(session.user.role) &&
               (audit.status === 'Submitted' || audit.status === 'Approved') && (
                <div className="flex items-center gap-2">
                  <Button 
                    onClick={() => reopenMutation.mutate()}
                    disabled={reopenMutation.isPending}
                    variant="secondary"
                    className="gap-2"
                    data-testid="button-reopen-audit"
                  >
                    <RotateCcw className="w-4 h-4" />
                    {reopenMutation.isPending ? 'Reopening...' : 'Reopen'}
                  </Button>

                  {/* Approve / Reject controls for in-scope managers */}
                  {audit.status === 'Submitted' && canApprove && (
                    <>
                      <Button
                        onClick={() => approveMutation.mutate()}
                        disabled={approveMutation.isPending}
                        className="gap-2"
                        data-testid="button-approve-audit"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {approveMutation.isPending ? 'Approving...' : 'Approve'}
                      </Button>

                      <Button
                        onClick={() => {
                          const notes = prompt('Enter rejection notes (required):') || '';
                          if (!notes.trim()) {
                            toast({ title: 'Validation', description: 'Rejection notes are required', variant: 'destructive' });
                            return;
                          }
                          rejectMutation.mutate(notes);
                        }}
                        disabled={rejectMutation.isPending}
                        variant="destructive"
                        className="gap-2"
                        data-testid="button-reject-audit"
                      >
                        <XCircle className="w-4 h-4" />
                        {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Assigned to another user notice and quick actions (managers) */}
          {audit.status === 'Draft' && !canEditDraft && (
            <div className="mb-4 p-4 rounded-lg border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 flex items-center justify-between">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">This audit is assigned to another user.</p>
                  <p className="text-xs text-muted-foreground">You are viewing in read-only mode.</p>
                </div>
              </div>
              {canManageTakeover && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setOpenAssignedModal(true)}>
                    Manage access
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleNotifyAssignee} data-testid="button-notify-assignee-inline">
                    <Bell className="w-4 h-4 mr-1" />
                    Notify
                  </Button>
                  <Button size="sm" onClick={handleTakeOver} data-testid="button-takeover-inline">
                    Take Over
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Status and Dates Row */}
          <div className="flex items-center gap-6 mb-6 pb-6 border-b">
            <Badge 
              variant={audit.status === 'Approved' ? 'default' : 'secondary'}
              className="gap-1 text-base py-1.5 px-3"
            >
              <CheckCircle className="w-4 h-4" />
              {audit.status}
            </Badge>
            
            {audit.created_at && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="font-medium">Created:</span>
                <span>{format(new Date(audit.created_at), "dd/MM/yyyy")}</span>
              </div>
            )}
            
            {audit.submitted_at && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="font-medium">Submitted:</span>
                <span>{format(new Date(audit.submitted_at), "dd/MM/yyyy")}</span>
              </div>
            )}
            
            {audit.approved_at && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="font-medium">Approved:</span>
                <span>{format(new Date(audit.approved_at), "dd/MM/yyyy")}</span>
              </div>
            )}

            {audit.reopened_at && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="font-medium">Re-opened:</span>
                <span>{format(new Date(audit.reopened_at), "dd/MM/yyyy")}</span>
              </div>
            )}
          </div>
          
          {/* Progress & Score Bars */}
          <div>
            <div className="mb-2">
              <AuditProgressAndScore
                status={audit?.status}
                completionPercentage={progressPercent}
                score={audit?.score}
              />
            </div>
          </div>
        </Card>
        
        {/* Modal: Open actions blocker */}
        {openActionsModal && (
          <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpenActionsModal(false)} />
            <div className="relative z-10 w-full max-w-md rounded-lg border bg-card p-6 shadow-lg">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-amber-500 mt-[2px]" />
                <div>
                  <h3 className="text-lg font-semibold">Actions need attention</h3>
                  <p className="text-sm text-muted-foreground mt-1">This audit has open actions (Pending or In Progress). Please complete all actions before approving.</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpenActionsModal(false)}>Close</Button>
                <Button onClick={() => setLocation(`/actions?auditId=${auditId}`)} data-testid="button-go-to-actions">
                  View Actions
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Assigned to another user (manager options) */}
        {openAssignedModal && (
          <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpenAssignedModal(false)} />
            <div className="relative z-10 w-full max-w-md rounded-lg border bg-card p-6 shadow-lg">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-amber-500 mt-[2px]" />
                <div>
                  <h3 className="text-lg font-semibold">This audit has been assigned to another user</h3>
                  <p className="text-sm text-muted-foreground mt-1">You can view only, notify the assignee, or take over to edit.</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpenAssignedModal(false)} data-testid="button-view-only-modal">View only</Button>
                {canManageTakeover && (
                  <>
                    <Button variant="outline" onClick={handleNotifyAssignee} data-testid="button-notify-assignee-modal">
                      <Bell className="w-4 h-4 mr-1" />
                      Notify
                    </Button>
                    <Button onClick={handleTakeOver} data-testid="button-takeover-modal">Take Over</Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Questions and Responses */}
        <div className="space-y-4">
          {items.map((item: any, idx: number) => {
            // Render sections as headers
            if (item.type === 'section') {
              return (
                <div key={item.id} className="py-4" data-testid={`section-${idx}`}>
                  <h3 className="text-lg font-semibold text-foreground border-b pb-2">
                    {item.label}
                  </h3>
                </div>
              );
            }
            
            // Skip headings and dividers
            if (item.type === 'heading' || item.type === 'divider') {
              return null;
            }
            
            const response = responses[item.id];
            const questionNumber = answerableItems.findIndex((ai: any) => ai.id === item.id) + 1;
            
            return (
              <Card 
                key={item.id} 
                className="p-6"
                data-testid={`card-response-${idx}`}
              >
                {/* Question */}
                <div className="mb-4">
                  <div className="flex items-start gap-3 mb-2">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                      {questionNumber}
                    </span>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground">{item.label}</h3>
                      {item.description && (
                        <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Response Display */}
                <div className="ml-11">
                  {item.type === 'ynna_actions_review' && (
                    <div className="space-y-4">
                      {/* Answer */}
                      {response?.answer ? (
                        <div className="flex items-center gap-2">
                          {response.answer === 'Yes' && (
                            <Badge variant="default" className="gap-1 bg-green-600">
                              <CheckCircle className="w-4 h-4" />
                              Yes
                            </Badge>
                          )}
                          {response.answer === 'No' && (
                            <Badge variant="destructive" className="gap-1">
                              <XCircle className="w-4 h-4" />
                              No
                            </Badge>
                          )}
                          {response.answer === 'N/A' && (
                            <Badge variant="secondary" className="gap-1">
                              <Minus className="w-4 h-4" />
                              N/A
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No answer provided</p>
                      )}
                      
                      {/* Actions */}
                      {item.actions_enabled && response?.answer && response.actions && (
                        <div className="pt-3 border-t">
                          <p className="text-sm font-medium mb-2">Actions Required:</p>
                          {response.actions.noActionNeeded ? (
                            <div className="text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/20 p-2 rounded">
                              ✓ No actions needed
                            </div>
                          ) : response.actions.actionText ? (
                            <div className="text-sm bg-muted p-3 rounded">
                              {response.actions.actionText}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">No actions specified</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {item.type === 'text' && (
                    response ? (
                      <div className="text-sm bg-muted p-3 rounded">
                        {typeof response === 'object' ? response.answer : response}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">No answer provided</p>
                    )
                  )}
                  
                  {item.type === 'textarea' && (
                    response ? (
                      <div className="text-sm bg-muted p-3 rounded whitespace-pre-wrap">
                        {typeof response === 'object' ? response.answer : response}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">No answer provided</p>
                    )
                  )}
                  
                  {(item.type === 'radio' || item.type === 'checkbox') && (
                    <div>
                      {Array.isArray(response) && response.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {response.map((opt: string) => (
                            <Badge key={opt} variant="outline">{opt}</Badge>
                          ))}
                        </div>
                      ) : response ? (
                        <Badge variant="outline">
                          {typeof response === 'object' ? response.answer : response}
                        </Badge>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No answer provided</p>
                      )}
                    </div>
                  )}
                  
                  {(item.type === 'number' || item.type === 'date') && (
                    response ? (
                      <div className="text-sm bg-muted p-3 rounded">
                        {typeof response === 'object' ? response.answer : response}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">No answer provided</p>
                    )
                  )}
                  
                  {/* Action Items for this question */}
                  {(() => {
                    const itemActions = actionItems.filter((action: any) => action.item_id === item.id);
                    if (itemActions.length === 0) return null;
                    
                    return (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <ClipboardList className="w-4 h-4 text-primary" />
                          Action Items
                        </div>
                        {itemActions.map((action: any) => (
                          <div key={action.id} className="border rounded-lg p-3 bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800" data-testid={`action-item-${action.id}`}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <p className="text-sm text-foreground">{action.action_description}</p>
                              </div>
                              <Badge variant="outline" className="ml-3">
                                {action.status || 'Pending'}
                              </Badge>
                            </div>
                            
                            {/* Review Information */}
                            {action.review_notes && (
                              <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-800">
                                <p className="text-xs font-medium text-muted-foreground mb-1">Reviewer Notes:</p>
                                <div className="text-xs bg-background p-2 rounded">
                                  {action.review_notes}
                                </div>
                              </div>
                            )}
                            
                            {/* Action Status */}
                            <div className="flex items-center gap-4 mt-2 pt-2 border-t border-amber-200 dark:border-amber-800">
                              {action.actioned_at && (
                                <div className="flex items-center gap-1 text-xs">
                                  <CheckCircle2 className="w-3 h-3 text-blue-600" />
                                  <span className="text-muted-foreground">
                                    Actioned on {format(new Date(action.actioned_at), "dd/MM/yyyy")}
                                  </span>
                                </div>
                              )}
                              
                              {action.acknowledged && (
                                <div className="flex items-center gap-1 text-xs">
                                  <CheckCircle2 className="w-3 h-3 text-green-600" />
                                  <span className="text-green-600 dark:text-green-400 font-medium">
                                    Acknowledged on {format(new Date(action.acknowledged_at), "dd/MM/yyyy")}
                                  </span>
                                </div>
                              )}
                              
                              {action.reviewed_by_user && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <User className="w-3 h-3" />
                                  <span>Reviewed by {action.reviewed_by_user.name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
      
      <ScrollNavigation />
    </div>
  );
}
